/*
 * CURTAIN BUILDER
 *
 * Drives the `curtain-builder` section: finial kit (this product) + rod tubing + center brackets + rings.
 * Everything product-specific comes from the section's JSON config (see snippets/curtain-builder-form.liquid);
 * nothing here knows SKUs, handles or finishes.
 *
 * Pure helpers are exported for tests; the custom element only registers in the browser.
 */

export function fillPattern(pattern, values) {
  return String(pattern || '').replace(/\{(\w+)\}/g, (_, key) => (values[key] ?? '').toString());
}

export function stripSuffix(value, suffix) {
  const text = String(value || '');
  return suffix && text.endsWith(suffix) ? text.slice(0, -suffix.length) : text;
}

export function buildSku({ pattern, kitSku, kitSuffix, material, length }) {
  return fillPattern(pattern, { kit: stripSuffix(kitSku, kitSuffix), material: material || '', length: length ?? '' });
}

export function imageNames({ pattern, sku, count }) {
  const names = [];
  for (let n = 1; n <= count; n += 1) names.push(fillPattern(pattern, { sku, n }));
  return names;
}

// Rules are [{ from, qty }]; the highest `from` the length reaches wins. `from` <= 0 disables a rule.
export function centerBracketQty(length, rules) {
  return (rules || [])
    .filter((rule) => rule.from > 0 && length >= rule.from)
    .reduce((qty, rule) => Math.max(qty, rule.qty), 0);
}

// Tubing always takes the finial's finish: the family member whose finish matches, or the only product when a
// material has a single one (lucite, wood). No match means the material isn't offered in this finish.
export function pickFinishProduct(products, finish) {
  if (!products || products.length === 0) return null;
  if (products.length === 1) return products[0];
  return products.find((product) => product.finish === finish) || null;
}

// Shopify caps a cart line (and a bundle component) at 2000 units, so the rod inches set the real quantity limit.
export const MAX_LINE_UNITS = 2000;

export function maxQuantityFor({ length, bracketQty, rings, configuredMax }) {
  const largestPart = Math.max(length, bracketQty, rings, 1);
  return Math.max(1, Math.min(configuredMax, Math.floor(MAX_LINE_UNITS / largestPart)));
}

// A build ordered N times goes to the cart as N separate sets so the bundle cart transform can merge each one
// (Shopify can't set a merged line's quantity). Set 1 keeps the plain bundle id, later sets get `#k`; numbering
// continues after sets of the same build already in the cart so repeat adds never merge into an existing set.
export function setBundleIds(baseId, existingIds, count) {
  const taken = existingIds
    .map((id) => (id === baseId ? 1 : id.startsWith(`${baseId}#`) ? parseInt(id.slice(baseId.length + 1), 10) : 0))
    .filter((n) => n > 0);
  const start = taken.length ? Math.max(...taken) + 1 : 1;
  return Array.from({ length: count }, (_, i) => (start + i === 1 ? baseId : `${baseId}#${start + i}`));
}

export function baseBundleId(id) {
  return String(id || '').split('#')[0];
}

// Visible details of a build: the kit line's properties and, via `_bundle_attributes`, the merged bundle line.
export function visibleProperties(props) {
  return Object.fromEntries(Object.entries(props).filter(([key]) => !key.startsWith('_')));
}

// Size values smallest first (1.75" before 2"), whatever order the product lists them in.
export function sortSizes(values) {
  return [...values].sort((a, b) => parseFloat(a) - parseFloat(b));
}

// Summary name of the kit: "Finial Set – 1.75"" for finials, "End Cap Set" (no size) for end caps.
export function kitSummaryLabel({ isEndCap, finialLabel, endCapLabel, size }) {
  if (isEndCap) return endCapLabel;
  return size ? `${finialLabel} – ${size}` : finialLabel;
}

// Units of every part for `quantity` identical builds (tubing is sold per inch).
export function bundleQuantities({ length, bracketQty, rings, quantity }) {
  return { kit: quantity, tubing: length * quantity, brackets: bracketQty * quantity, rings: rings * quantity };
}

// Out of stock only blocks when Shopify would: tracked inventory with "deny" policy.
export function variantInStock(variant, quantity = 1) {
  if (!variant || !variant.available) return false;
  if (variant.tracked && variant.policy === 'deny') return variant.qty >= quantity;
  return true;
}

// Picks the variant whose options contain every wanted value (order-insensitive, so products can
// name/order their diameter option differently).
export function findVariant(product, wanted) {
  if (!product) return null;
  const values = wanted.filter(Boolean);
  return product.variants.find((variant) => values.every((value) => variant.options.includes(value))) || null;
}

// An unset/empty variant list keeps the configured materials. Unknown entries never broaden a restriction.
export function materialAllowed(material, variant) {
  if (!material) return false;
  const allowed = variant?.allowedRodMaterials;
  if (allowed == null || (Array.isArray(allowed) && allowed.length === 0)) return true;
  if (!Array.isArray(allowed)) return false;
  const key = String(material.allowedValue || material.label || '')
    .trim()
    .toLowerCase();
  return allowed.some((value) => String(value).trim().toLowerCase() === key);
}

// The kit option holding the rod diameter: the configured name, else the option whose values are all
// tubing diameters (end caps call it "Size").
export function kitDiameterIndex(kit, optionName, diameters) {
  const byName = kit.options.findIndex((name) => name.toLowerCase() === String(optionName || '').toLowerCase());
  if (byName >= 0) return byName;
  return kit.options.findIndex((_, index) => {
    const values = unique(kit.variants.map((variant) => variant.options[index]));
    return values.length > 0 && values.every((value) => diameters.includes(value));
  });
}

// Diameters every rod material offers (drops per-material values such as a lucite "Clear" colour),
// sorted numerically so 1" comes before 1.25".
export function sharedDiameters(materials) {
  const perMaterial = materials
    .map((material) => unique(material.products.flatMap((p) => p.variants.flatMap((v) => v.options))))
    .filter((values) => values.length > 0);
  if (perMaterial.length === 0) return [];
  return perMaterial[0]
    .filter((value) => perMaterial.every((values) => values.includes(value)))
    .sort((a, b) => parseFloat(a) - parseFloat(b));
}

export function snapLength(value, { min, max, step }) {
  const maxOnGrid = min + Math.floor((max - min) / step) * step;
  if (Number.isNaN(value) || value <= min) return min;
  if (value >= max) return maxOnGrid;
  return Math.min(maxOnGrid, min + Math.round((value - min) / step) * step);
}

export function unique(values) {
  return [...new Set(values.filter((value) => value != null && value !== ''))];
}

// Shopify money formats, with thousands separators; whole amounts drop the ".00" (e.g. $1,264, $1,264.50).
export function formatMoney(cents, format) {
  const group = (whole, separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  const amount = (thousands, decimal) => {
    const whole = group(String(Math.floor(cents / 100)), thousands);
    const fraction = String(cents % 100).padStart(2, '0');
    return cents % 100 === 0 ? whole : `${whole}${decimal}${fraction}`;
  };
  const rounded = (thousands) => group(String(Math.round(cents / 100)), thousands);
  return String(format || '${{amount}}')
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, rounded('.'))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount('.', ','))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, rounded(','))
    .replace(/\{\{\s*amount\s*\}\}/g, amount(',', '.'));
}

const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

const sized = (url, width) => `${url}${url.includes('?') ? '&' : '?'}width=${width}`;

// Extensions to try, with the one that already matched first.
export function preferExtension(extensions, preferred) {
  return preferred ? [preferred, ...extensions.filter((extension) => extension !== preferred)] : extensions;
}

export const HERO_WIDTH = 1600;
const THUMB_WIDTH = 240;

// Main image on top, thumbnails underneath. The thumbnail row is always rendered (empty with fewer than 2 images)
// so its height stays reserved and switching options never shifts the layout.
export function galleryHtml(slides) {
  if (slides.length === 0) return '';
  const [first] = slides;
  const hero = `<div class="curtain-builder__hero">
      <img data-cb-hero src="${escapeHtml(sized(first.url, HERO_WIDTH))}" alt="${escapeHtml(first.alt || '')}" width="${HERO_WIDTH}" height="${HERO_WIDTH}">
    </div>`;
  const thumbs =
    slides.length < 2
      ? ''
      : slides
          .map(
            (
              slide,
              index,
            ) => `<button type="button" class="curtain-builder__thumb" data-cb-thumb="${escapeHtml(sized(slide.url, HERO_WIDTH))}"
          aria-current="${index === 0}" aria-label="${escapeHtml(slide.alt || '')} ${index + 1}">
          <img src="${escapeHtml(sized(slide.url, THUMB_WIDTH))}" alt="${escapeHtml(slide.alt || '')}" loading="lazy" width="${THUMB_WIDTH}" height="${THUMB_WIDTH}">
        </button>`,
          )
          .join('');
  return `${hero}<div class="curtain-builder__thumbs">${thumbs}</div>`;
}

if (typeof window !== 'undefined' && !window.customElements.get('curtain-builder')) {
  const imageCache = new Map();

  // Resolves once an <img> has loaded (or failed), so the blur never gets stuck.
  const imageReady = (img) =>
    !img || img.complete
      ? Promise.resolve()
      : new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });

  const probeImage = (url) => {
    if (!imageCache.has(url)) {
      imageCache.set(
        url,
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = url;
        }),
      );
    }
    return imageCache.get(url);
  };

  class CurtainBuilder extends HTMLElement {
    connectedCallback() {
      if (this.config) {
        this.render();
        return;
      }
      this.config = JSON.parse(this.querySelector('[data-cb-config]').textContent);
      this.kit = this.config.kit;
      this.renderedInfoVariant = this.config.kitSelectedId;
      this.diameters = sharedDiameters(this.config.materials);
      this.diameterIndex = kitDiameterIndex(this.kit, this.config.kitDiameterOption, this.diameters);
      this.sizeIndex = this.kit.options.findIndex((_, index) => index !== this.diameterIndex);
      this.isEndCap = this.sizeIndex < 0;
      this.gallery = document.querySelector(`[data-cb-gallery="${this.config.sectionId}"]`);
      this.gallery?.addEventListener('click', (event) => this.onThumbClick(event));
      this.state = this.initialState();
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.querySelector('[data-cb-add]')?.addEventListener('click', () => this.addToCart());
      this.render();
    }

    // --- State ------------------------------------------------------------------------------------

    initialState() {
      const { min, max, step, defaultLength } = this.config.length;
      const variant = this.kit.variants.find((v) => v.id === this.config.kitSelectedId) || this.kit.variants[0];
      const stored = this.readStorage();
      const fromUrl = new URL(window.location.href).searchParams.has('variant');
      const kitOptions = {};
      this.kit.options.forEach((name, index) => {
        const smallest = sortSizes(unique(this.kit.variants.map((v) => v.options[index])))[0];
        const fallback = fromUrl || index === this.diameterIndex ? variant.options[index] : smallest;
        kitOptions[name] = (!fromUrl && stored.kitOptions?.[name]) || fallback;
      });
      const materialIndex = Math.max(
        0,
        this.config.materials.findIndex((m) => m.code === stored.material),
      );
      return {
        kitOptions,
        materialIndex,
        diameter: (!fromUrl && stored.diameter) || variant.options[this.diameterIndex] || this.diameters[0],
        length: snapLength(parseInt(stored.length ?? defaultLength, 10), { min, max, step }),
        rings: Math.min(parseInt(stored.rings, 10) || 0, this.config.rings.maxSets),
        quantity: Math.max(1, parseInt(stored.quantity, 10) || 1),
      };
    }

    readStorage() {
      try {
        return JSON.parse(localStorage.getItem(this.config.storageKey)) || {};
      } catch {
        return {};
      }
    }

    writeStorage() {
      const s = this.state;
      const payload = {
        kitOptions: s.kitOptions,
        // Preserve the shopper's choice rather than a variant's forced fallback.
        material: this.config.materials[s.materialIndex]?.code,
        diameter: s.diameter,
        length: s.length,
        rings: s.rings,
        quantity: s.quantity,
      };
      try {
        localStorage.setItem(this.config.storageKey, JSON.stringify(payload));
      } catch {
        /* storage blocked: selections simply won't carry across finishes */
      }
    }

    // --- Derived selection --------------------------------------------------------------------------

    material(variant) {
      return this.config.materials[this.materialIndexFor(variant)] || null;
    }

    materialIndexFor(variant, diameter = this.state.diameter) {
      const { materials } = this.config;
      if (materialAllowed(materials[this.state.materialIndex], variant)) return this.state.materialIndex;
      const fallback = this.materialFallback;
      const fallbackIndex = materials.indexOf(fallback?.material);
      if (
        fallbackIndex >= 0 &&
        fallback.variantId === variant?.id &&
        fallback.preference === this.state.materialIndex &&
        fallback.diameter === diameter &&
        materialAllowed(fallback.material, variant)
      )
        return fallbackIndex;
      const allowed = materials.flatMap((material, index) => (materialAllowed(material, variant) ? [index] : []));
      const sellable = allowed.find((index) =>
        variantInStock(findVariant(this.tubingProduct(materials[index]), [diameter]), this.tubingUnits()),
      );
      return sellable ?? allowed[0] ?? -1;
    }

    tubingProduct(material) {
      if (!material) return null;
      return pickFinishProduct(material.products, this.kit.finish);
    }

    kitValues(overrides = {}) {
      const values = { ...this.state.kitOptions, ...overrides };
      if (this.diameterIndex >= 0)
        values[this.kit.options[this.diameterIndex]] = overrides.__diameter ?? this.state.diameter;
      return this.kit.options.map((name) => values[name]);
    }

    selection() {
      const { length, rings, diameter, quantity } = this.state;
      const kitVariant = findVariant(this.kit, this.kitValues());
      const material = this.material(kitVariant);
      const tubing = findVariant(this.tubingProduct(material), [diameter]);
      const bracketQty = centerBracketQty(length, this.config.brackets.rules);
      const bracketProduct = this.config.brackets.products.find((p) => p.finish === this.kit.finish);
      const bracket = bracketQty > 0 ? findVariant(bracketProduct, [diameter]) || bracketProduct?.variants[0] : null;
      const ringProduct = this.config.rings.products.find((p) => p.finish === this.kit.finish);
      const ring = rings > 0 ? findVariant(ringProduct, [diameter]) || ringProduct?.variants[0] : null;

      const sku = buildSku({
        pattern: this.config.sku.pattern,
        kitSku: kitVariant?.sku,
        kitSuffix: this.config.sku.kitSuffix,
        material: material?.code,
        length,
      });

      const imageSku = buildSku({
        pattern: this.config.sku.pattern,
        kitSku: kitVariant?.sku,
        kitSuffix: this.config.sku.kitSuffix,
        material: material?.code,
        length: this.config.images.length ?? length,
      });

      const units = bundleQuantities({ length, bracketQty, rings, quantity });
      const { labels } = this.config;
      const problems = [];
      if (!kitVariant || !variantInStock(kitVariant, units.kit)) problems.push(labels.kitUnavailable);
      if (!tubing || !variantInStock(tubing, units.tubing)) problems.push(labels.tubingUnavailable);
      if (bracketQty > 0 && !variantInStock(bracket, units.brackets)) problems.push(labels.bracketUnavailable);
      if (rings > 0 && !variantInStock(ring, units.rings)) problems.push(labels.ringsUnavailable);

      const unitTotal =
        (kitVariant?.price || 0) +
        (tubing?.price || 0) * length +
        (bracket?.price || 0) * bracketQty +
        (ring?.price || 0) * rings;
      const total = unitTotal * quantity;

      return {
        kitVariant,
        material,
        tubing,
        bracket,
        bracketQty,
        ring,
        rings,
        length,
        diameter,
        quantity,
        units,
        sku,
        imageSku,
        total,
        problems,
      };
    }

    // --- Events -------------------------------------------------------------------------------------

    onChange(event) {
      const input = event.target;
      const { cbGroup: group, cbValue: value } = input.dataset;
      if (!group) return;
      if (group === 'kit') this.state.kitOptions[input.dataset.cbOption] = value;
      if (group === 'diameter') this.state.diameter = value;
      if (group === 'material') this.state.materialIndex = parseInt(value, 10);
      if (group === 'length') this.setLength(parseInt(input.value, 10));
      if (group === 'rings')
        this.state.rings = Math.max(0, Math.min(parseInt(input.value, 10) || 0, this.config.rings.maxSets));
      if (group === 'quantity') this.setQuantity(parseInt(input.value, 10));
      this.render();
    }

    onClick(event) {
      const input = event.target.closest('[data-cb-group="material"]');
      if (input && !input.disabled) {
        const index = parseInt(input.dataset.cbValue, 10);
        const variant = findVariant(this.kit, this.kitValues());
        if (index !== this.state.materialIndex && this.config.materials[index] === this.material(variant)) {
          this.state.materialIndex = index;
          this.render();
          return;
        }
      }
      const button = event.target.closest('[data-cb-step]');
      if (!button) return;
      const target = button.dataset.cbStep;
      const delta = parseInt(button.dataset.cbDelta, 10);
      if (target === 'length') this.setLength(this.state.length + delta * this.config.length.step);
      if (target === 'rings')
        this.state.rings = Math.max(0, Math.min(this.state.rings + delta, this.config.rings.maxSets));
      if (target === 'quantity') this.setQuantity(this.state.quantity + delta);
      this.render();
    }

    maxQuantity() {
      const { length, rings } = this.state;
      const bracketQty = centerBracketQty(length, this.config.brackets.rules);
      return maxQuantityFor({ length, bracketQty, rings, configuredMax: this.config.maxQuantity });
    }

    setQuantity(value) {
      this.state.quantity = Math.max(1, Math.min(value || 1, this.maxQuantity()));
    }

    tubingUnits() {
      return this.state.length * this.state.quantity;
    }

    kitSize() {
      return this.sizeIndex >= 0 ? this.state.kitOptions[this.kit.options[this.sizeIndex]] : null;
    }

    setLength(value) {
      this.state.length = snapLength(value, this.config.length);
    }

    // --- Rendering ----------------------------------------------------------------------------------

    render() {
      this.setQuantity(this.state.quantity);
      const selection = this.selection();
      this.materialFallback = {
        variantId: selection.kitVariant?.id,
        preference: this.state.materialIndex,
        diameter: this.state.diameter,
        material: selection.material,
      };
      this.renderOptions(selection);
      this.renderSummary(selection);
      this.renderGallery(selection.imageSku);
      this.syncFinishLinks();
      this.renderProductInfo(selection.kitVariant);
      this.syncUrl(selection.kitVariant);
      this.writeStorage();
    }

    syncFinishLinks() {
      const products = this.config.finishProducts;
      if (!products?.length) return;
      const values = this.kitValues();
      for (const link of this.querySelectorAll('[data-cb-finishes] a')) {
        const path = link.dataset.cbProductUrl || new URL(link.href, window.location.href).pathname;
        const product = products.find((member) => new URL(member.url, window.location.href).pathname === path);
        if (!product) continue;
        link.dataset.cbProductUrl = path;
        const variant = product.variants.find(
          (candidate) =>
            product.options.length === this.kit.options.length &&
            this.kit.options.every((name, index) => {
              const targetIndex = product.options.findIndex((option) => option.toLowerCase() === name.toLowerCase());
              return targetIndex >= 0 && candidate.options[targetIndex] === values[index];
            }),
        );
        link.classList.toggle('is-disabled', !variant);
        if (!variant) {
          link.removeAttribute('href');
          link.setAttribute('role', 'link');
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('tabindex', '-1');
          continue;
        }
        const url = new URL(product.url, window.location.href);
        url.searchParams.set('variant', variant.id);
        link.setAttribute('href', url.pathname + url.search);
        link.removeAttribute('aria-disabled');
        link.removeAttribute('role');
        link.removeAttribute('tabindex');
      }
    }

    async renderProductInfo(variant) {
      const variantId = variant?.id;
      const root = this.closest('.curtain-builder-section');
      for (const inventory of root?.querySelectorAll('[data-block-type="inventory"]') || []) {
        inventory.hidden = variantId !== this.renderedInfoVariant;
      }
      if (variantId === this.pendingInfoVariant) return;
      this.infoRequest?.abort();
      this.pendingInfoVariant = variantId;
      if (!variantId || variantId === this.renderedInfoVariant) return;

      if (!root) return;
      const request = new AbortController();
      this.infoRequest = request;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.config.sectionId);

      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error(`Product details request failed (${response.status})`);
        const html = await response.text();
        if (request.signal.aborted || variantId !== this.pendingInfoVariant || !this.isConnected) return;
        const nextRoot = new DOMParser().parseFromString(html, 'text/html').querySelector('.curtain-builder-section');
        if (!nextRoot) throw new Error('Product details section is missing');
        const selector = ['inventory', 'badges', 'spec-item', 'accordion-tab', 'accordion-faqs']
          .map((type) => `[data-block-type="${type}"]`)
          .join(',');
        const replacements = Array.from(root.querySelectorAll(selector), (current) => {
          const next = nextRoot.querySelector(`[data-block-id="${current.dataset.blockId}"]`);
          if (!next) throw new Error(`Product details block is missing (${current.dataset.blockId})`);
          return { current, next, wasOpen: current.querySelector('details')?.open };
        });
        for (const { current, next, wasOpen } of replacements) {
          current.replaceWith(next);
          if (wasOpen) {
            const details = next.querySelector('details');
            if (details) {
              details.open = true;
              details.setAttribute('aria-expanded', 'true');
            }
          }
        }
        this.renderedInfoVariant = variantId;
      } catch (error) {
        if (!request.signal.aborted) console.warn('[curtain-builder] could not refresh product details:', error);
      } finally {
        if (this.infoRequest === request) this.pendingInfoVariant = null;
      }
    }

    disconnectedCallback() {
      this.infoRequest?.abort();
      this.pendingInfoVariant = null;
    }

    radioGroup({ name, legend, value, items }) {
      const id = `${this.config.sectionId}-${name}`;
      const inputs = items
        .map((item, index) => {
          const inputId = `${id}-${item.id ?? index}`;
          return `<input class="sr-only" type="radio" name="${id}" id="${inputId}" ${item.attrs}
              ${item.value === value ? 'checked' : ''} ${item.disabled ? 'disabled' : ''}>
            <label class="block-swatch ${item.disabled ? 'is-disabled' : ''}" for="${inputId}"><span>${escapeHtml(item.label)}</span></label>`;
        })
        .join('');
      return `<fieldset class="variant-picker__option curtain-builder__group">
          <div class="variant-picker__option-info h-stack gap-1">
            <legend class="block-label text-subdued">${escapeHtml(legend)}</legend>
          </div>
          <div class="variant-picker__option-values wrap gap-2">${inputs}</div>
        </fieldset>`;
    }

    stepper({ name, label, value, min, max, hint, tooltip }) {
      const id = `${this.config.sectionId}-${name}`;
      return `<div class="curtain-builder__group v-stack gap-2">
          <div class="curtain-builder__label-row">
            <label class="block-label text-subdued" for="${id}">${escapeHtml(label)}</label>
            ${tooltip ? this.tooltip(`${id}-tip`, label, tooltip) : ''}
          </div>
          <div class="quantity-selector">
            <button type="button" class="quantity-selector__button" data-cb-step="${name}" data-cb-delta="-1" aria-label="${escapeHtml(label)} −">${this.icons.minus}</button>
            <input id="${id}" class="quantity-selector__input" type="number" inputmode="numeric" data-cb-group="${name}"
              min="${min}" max="${max}" value="${value}" autocomplete="off">
            <button type="button" class="quantity-selector__button" data-cb-step="${name}" data-cb-delta="1" aria-label="${escapeHtml(label)} +">${this.icons.plus}</button>
          </div>
          ${hint ? `<p class="text-xs text-subdued">${escapeHtml(hint)}</p>` : ''}
        </div>`;
    }

    // Hover/focus tooltip next to a heading; the trigger is a button so keyboard users can reach it.
    tooltip(id, label, text) {
      return `<span class="curtain-builder__tip">
          <button type="button" class="curtain-builder__tip-trigger" aria-describedby="${id}" aria-label="${escapeHtml(label)}: ${escapeHtml(this.config.labels.moreInfo)}">${this.icons.info}</button>
          <span class="curtain-builder__tip-bubble text-xs" role="tooltip" id="${id}">${escapeHtml(text)}</span>
        </span>`;
    }

    get icons() {
      const template = this.querySelector('[data-cb-icons]');
      return {
        minus: template?.content.querySelector('[data-icon="minus"]')?.outerHTML || '−',
        plus: template?.content.querySelector('[data-icon="plus"]')?.outerHTML || '+',
        info: template?.content.querySelector('[data-icon="info"]')?.outerHTML || 'i',
      };
    }

    renderOptions({ kitVariant, material }) {
      const { labels, length } = this.config;
      const groups = [];

      // Finial options other than the diameter (e.g. finial Size). Disabled when no in-stock kit matches.
      this.kit.options.forEach((name, index) => {
        if (index === this.diameterIndex) return;
        const values = sortSizes(unique(this.kit.variants.map((v) => v.options[index])));
        groups.push(
          this.radioGroup({
            name: `kit-${index}`,
            legend: index === this.sizeIndex ? labels.finialSize : name,
            value: this.state.kitOptions[name],
            items: values.map((value) => ({
              value,
              label: value,
              attrs: `data-cb-group="kit" data-cb-option="${escapeHtml(name)}" data-cb-value="${escapeHtml(value)}"`,
              disabled: !variantInStock(findVariant(this.kit, this.kitValues({ [name]: value })), this.state.quantity),
            })),
          }),
        );
      });

      const diameterItems = this.diameters.map((value) => {
        const variant = findVariant(this.kit, this.kitValues({ __diameter: value }));
        const material = this.config.materials[this.materialIndexFor(variant, value)];
        return {
          value,
          label: value,
          attrs: `data-cb-group="diameter" data-cb-value="${escapeHtml(value)}"`,
          disabled:
            !variantInStock(variant, this.state.quantity) ||
            !variantInStock(findVariant(this.tubingProduct(material), [value]), this.tubingUnits()),
        };
      });

      if (this.diameterIndex >= 0 && this.config.showKitDiameter) {
        groups.push(
          this.radioGroup({
            name: 'kit-diameter',
            legend: this.isEndCap ? labels.endCapSize : this.kit.options[this.diameterIndex],
            value: this.state.diameter,
            items: diameterItems,
          }),
        );
      }

      const materialItems = this.config.materials.flatMap((material, index) =>
        materialAllowed(material, kitVariant)
          ? [
              {
                id: index,
                value: String(index),
                label: material.label,
                attrs: `data-cb-group="material" data-cb-value="${index}"`,
                disabled: !(this.tubingProduct(material)?.variants || []).some((v) =>
                  variantInStock(v, this.tubingUnits()),
                ),
              },
            ]
          : [],
      );
      if (materialItems.length > 0) {
        groups.push(
          this.radioGroup({
            name: 'material',
            legend: labels.material,
            value: String(this.config.materials.indexOf(material)),
            items: materialItems,
          }),
        );
      }

      groups.push(
        this.radioGroup({
          name: 'diameter',
          legend: labels.diameter,
          value: this.state.diameter,
          items: diameterItems,
        }),
      );

      groups.push(
        this.stepper({
          name: 'length',
          label: labels.length,
          value: this.state.length,
          min: length.min,
          max: length.max,
          hint: fillPattern(labels.lengthHint, { step: length.step, min: length.min, max: length.max }),
          tooltip: labels.lengthTooltip,
        }),
      );

      groups.push(
        this.stepper({
          name: 'quantity',
          label: labels.quantity,
          value: this.state.quantity,
          min: 1,
          max: this.maxQuantity(),
          hint: labels.quantityHint,
        }),
      );

      if (this.config.rings.products.length > 0) {
        groups.push(
          this.stepper({
            name: 'rings',
            label: labels.rings,
            value: this.state.rings,
            min: 0,
            max: this.config.rings.maxSets,
            hint: labels.ringsHint,
            tooltip: labels.ringsTooltip,
          }),
        );
      }

      const container = this.querySelector('[data-cb-options]');
      const focusedId = document.activeElement?.id;
      container.innerHTML = groups.join('');
      if (focusedId) document.getElementById(focusedId)?.focus();
    }

    renderSummary(selection) {
      const { labels, moneyFormat } = this.config;
      const money = (cents) => formatMoney(cents, moneyFormat);
      const kitLabel = kitSummaryLabel({
        isEndCap: this.isEndCap,
        finialLabel: labels.summaryFinials,
        endCapLabel: labels.summaryEndCaps,
        size: this.kitSize(),
      });
      const rodLabel = selection.material?.summaryLabel || selection.material?.label || '';
      const rows = [
        [kitLabel, selection.kitVariant ? money(selection.kitVariant.price) : '—'],
        [`${rodLabel} ${selection.length}"`, selection.tubing ? money(selection.tubing.price * selection.length) : '—'],
        [labels.summaryEndBrackets, labels.included],
      ];
      if (selection.bracketQty > 0) {
        rows.push([
          `${labels.summaryCenterBrackets} × ${selection.bracketQty}`,
          selection.bracket?.price ? money(selection.bracket.price * selection.bracketQty) : labels.included,
        ]);
      }
      if (selection.rings > 0) {
        rows.push([
          `${labels.summaryRings} × ${selection.rings}`,
          selection.ring ? money(selection.ring.price * selection.rings) : '—',
        ]);
      }

      if (selection.quantity > 1) rows.push([labels.quantity, `× ${selection.quantity}`]);

      this.querySelector('[data-cb-summary]').innerHTML = rows
        .map(
          ([label, value]) =>
            `<div class="h-stack justify-between gap-4"><span class="text-subdued">${escapeHtml(label)}</span><span>${escapeHtml(value)}</span></div>`,
        )
        .join('');
      this.querySelector('[data-cb-total]').textContent = money(selection.total);
      this.querySelector('[data-cb-sku]').textContent = selection.sku;

      const problems = this.querySelector('[data-cb-problems]');
      problems.hidden = selection.problems.length === 0;
      problems.textContent = selection.problems.join(' ');

      const button = this.querySelector('[data-cb-add]');
      button.disabled = selection.problems.length > 0;
      button.querySelector('[data-cb-add-label]').textContent =
        selection.problems.length > 0 ? labels.soldOut : labels.addToCart;
    }

    async renderGallery(sku) {
      if (!this.gallery || !sku || sku === this.renderedSku) return;
      this.renderedSku = sku;
      // Keep the current image on screen, blurred, until the next one is ready.
      this.gallery.classList.add('is-loading');
      const { images } = this.config;
      const names = imageNames({ pattern: images.pattern, sku, count: images.count });

      // Every miss is a 404 in the browser console, so request as little as possible: image 1 first, and when it
      // doesn't exist skip the rest (a build without a main image has no others). Later images try image 1's
      // file type first.
      const find = async (name, extensions) => {
        for (const extension of extensions) {
          const url = `${images.filesBase}${name}.${extension}`;
          if (await probeImage(sized(url, HERO_WIDTH))) return { url, extension };
        }
        return null;
      };
      const first = await find(names[0], images.extensions);
      const rest = [];
      if (first) {
        const extensions = preferExtension(images.extensions, first.extension);
        for (let i = 1; i < names.length; i += 2) {
          const pair = await Promise.all(names.slice(i, i + 2).map((name) => find(name, extensions)));
          rest.push(...pair);
          if (pair.every((hit) => !hit)) break; // two missing in a row: no more images for this build
        }
      }
      const found = [first, ...rest].map((hit) => hit?.url || null);
      if (sku !== this.renderedSku) return; // a newer selection won the race

      const urls = found.filter(Boolean);
      // Report the main image or gaps before the last image found, not the numbers checked past the end.
      const lastFound = found.reduce((last, url, index) => (url ? index : last), -1);
      const missing = names.filter((_, index) => (index === 0 ? !found[0] : index < lastFound && !found[index]));
      if (missing.length > 0) console.warn('[curtain-builder] missing images in Files:', missing.join(', '));

      const slides = urls.length > 0 ? urls.map((url) => ({ url, alt: this.config.productTitle })) : images.fallback;
      this.gallery.innerHTML = galleryHtml(slides);
      this.settleImages();
      await imageReady(this.gallery.querySelector('[data-cb-hero]'));
      if (sku === this.renderedSku) this.gallery.classList.remove('is-loading');
    }

    // Thumbnails blur until their own file arrives.
    settleImages() {
      this.gallery.querySelectorAll('.curtain-builder__thumb img').forEach((img) => {
        if (img.complete) return;
        img.classList.add('is-loading');
        imageReady(img).then(() => img.classList.remove('is-loading'));
      });
    }

    onThumbClick(event) {
      const thumb = event.target.closest('[data-cb-thumb]');
      if (!thumb) return;
      const hero = this.gallery.querySelector('[data-cb-hero]');
      if (hero.src === thumb.dataset.cbThumb) return;
      this.gallery.classList.add('is-loading');
      hero.src = thumb.dataset.cbThumb;
      hero.alt = thumb.querySelector('img')?.alt || '';
      imageReady(hero).then(() => this.gallery.classList.remove('is-loading'));
      this.gallery.querySelectorAll('[data-cb-thumb]').forEach((el) => {
        el.setAttribute('aria-current', String(el === thumb));
      });
    }

    syncUrl(variant) {
      if (!variant || !this.config.updateUrl) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') === String(variant.id)) return;
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    // --- Cart ---------------------------------------------------------------------------------------

    // One set of the build: 1 kit, the rod inches, its brackets and ring sets.
    lineItems(selection, bundleId) {
      const { labels } = this.config;
      const child = (role, id, quantity) => ({
        id,
        quantity,
        properties: {
          _bundle_id: bundleId,
          _bundle_role: role,
          _bundle_type: this.config.bundleType,
          _bundle_parent_variant: String(selection.kitVariant.id),
        },
      });

      const parentProps = {
        [labels.propSku]: selection.sku,
        [this.isEndCap ? labels.endCapSize : labels.finialSize]: this.isEndCap ? selection.diameter : this.kitSize(),
        [labels.material]: selection.material.label,
        [labels.diameter]: selection.diameter,
        [labels.length]: `${selection.length}"`,
        // Stable key for the LTL freight delivery customization; the visible label above is merchant-editable.
        _rod_length: String(selection.length),
        _bundle_id: bundleId,
        _bundle_role: 'parent',
        _bundle_type: this.config.bundleType,
      };
      if (selection.bracketQty > 0) parentProps[labels.summaryCenterBrackets] = String(selection.bracketQty);
      if (selection.rings > 0) parentProps[labels.summaryRings] = String(selection.rings);
      if (this.config.finalSaleVariantIds.includes(selection.kitVariant.id)) {
        parentProps['Final Sale'] = this.config.finalSaleText;
      }
      // The merged bundle line keeps only these attributes: the visible details, the rod length, the build's plain id and one set
      // per bundle, so every set of the same build merges into an identical bundle line.
      parentProps._bundle_attributes = JSON.stringify({
        ...visibleProperties(parentProps),
        _rod_length: parentProps._rod_length,
        _bundle_id: baseBundleId(bundleId),
        _bundle_qty: '1',
      });

      const items = [
        { id: selection.kitVariant.id, quantity: 1, properties: parentProps },
        child('length', selection.tubing.id, selection.length),
      ];
      if (selection.bracketQty > 0) items.push(child('bracket', selection.bracket.id, selection.bracketQty));
      if (selection.rings > 0) items.push(child('rings', selection.ring.id, selection.rings));
      return items;
    }

    // The bundle id is the built SKU. If the cart already holds that SKU built differently (e.g. another
    // rod finish), suffix it so the cart never sums two different builds under one parent.
    bundleIdFor(cart, sku, parentProps) {
      const visible = (props) =>
        JSON.stringify(
          Object.entries(props)
            .filter(([key]) => !key.startsWith('_'))
            .sort(),
        );
      let candidate = sku;
      for (let n = 2; ; n += 1) {
        const parent = cart.items.find(
          (item) =>
            baseBundleId(item.properties?._bundle_id) === candidate && item.properties?._bundle_role === 'parent',
        );
        if (!parent || visible(parent.properties) === visible(parentProps)) return candidate;
        candidate = `${sku}-${n}`;
      }
    }

    async addToCart() {
      const selection = this.selection();
      if (selection.problems.length > 0) return;

      const root = window.Shopify?.routes?.root || '/';
      const button = this.querySelector('[data-cb-add]');
      button.setAttribute('aria-busy', 'true');

      try {
        const before = await (await fetch(`${root}cart.js`)).json();
        const draft = this.lineItems(selection, selection.sku);
        const baseId = this.bundleIdFor(before, selection.sku, draft[0].properties);
        const existingIds = before.items.map((item) => item.properties?._bundle_id).filter(Boolean);
        const bundleIds = setBundleIds(baseId, existingIds, selection.quantity);
        const items = bundleIds.flatMap((bundleId) => this.lineItems(selection, bundleId));

        const sections = ['variant-added'];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }),
        );

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items, sections: sections.join(',') }),
        });
        const json = await response.json();

        if (!response.ok) {
          await this.rollback(root, before, bundleIds);
          this.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: { error: json.description || this.config.labels.addError },
            }),
          );
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        if (window.themeVariables?.settings?.cartType === 'page') {
          window.location.href = `${root}cart`;
          return;
        }

        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = json.sections;
        this.dispatchEvent(
          new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } }),
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }),
        );
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    // Shopify can add some lines of a multi-item request before rejecting the rest; restore the
    // bundle's lines to their pre-add quantities so no orphaned child is left behind.
    async rollback(root, before, bundleIds) {
      const previous = new Map(before.items.map((item) => [item.key, item.quantity]));
      const after = await (await fetch(`${root}cart.js`)).json();
      const updates = {};
      after.items.forEach((item) => {
        if (!bundleIds.includes(item.properties?._bundle_id)) return;
        const quantity = previous.get(item.key) || 0;
        if (quantity !== item.quantity) updates[item.key] = quantity;
      });
      if (Object.keys(updates).length === 0) return;
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }
  }

  window.customElements.define('curtain-builder', CurtainBuilder);
}
